import { useRef, useState } from 'react';
import axiosInstance from '../../api/axiosInstance';
import styles from './PortfolioAutoFillBox.module.css';

/** 서버 실패를 사용자 문구로. axios 기본 문구("Request failed with status code 500")는 보여주지 않는다. */
export function portfolioErrorMessage(e) {
  const status = e?.response?.status;
  if (status === 413) return '파일 용량이 너무 큽니다. 용량을 줄이거나 다른 파일로 다시 시도해주세요.';
  if (status === 415) return 'PDF 또는 PPTX 파일만 지원합니다.';
  if (e?.response) return e.response.data?.message || '파일 분석 중 오류가 발생했습니다.';
  return e?.message || '파일 분석 중 오류가 발생했습니다.';
}

export default function PortfolioAutoFillBox({ onParsed }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleFile = async (file) => {
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'ppt', 'pptx'].includes(ext)) {
      setErrorMsg('PDF 또는 PPTX 파일만 지원합니다.');
      setStatus('error');
      return;
    }

    const fileType = ext === 'pdf' ? 'pdf' : 'pptx';
    setFileName(file.name);
    setStatus('loading');
    setErrorMsg('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);

      const res = await axiosInstance.post('/api/parse-portfolio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!res.data.success) {
        throw new Error(res.data.message || '파싱 실패');
      }

      setStatus('success');
      onParsed?.(res.data.data);
    } catch (e) {
      setStatus('error');
      setErrorMsg(portfolioErrorMessage(e));
    }
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const isLoading = status === 'loading';

  return (
    <div
      className={styles.box}
      role="button"
      tabIndex={0}
      onClick={() => !isLoading && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!isLoading && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      style={isLoading ? { cursor: 'default', opacity: 0.7 } : {}}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".ppt,.pptx,.pdf,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
        className={styles.input}
        onChange={handleChange}
        disabled={isLoading}
      />

      <div className={styles.iconWrap} aria-hidden="true">
        {isLoading ? (
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#E9E9EC" strokeWidth="2" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="#FFA40D" strokeWidth="2" strokeLinecap="round">
              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
            </path>
          </svg>
        ) : status === 'success' ? (
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#FFFBD5" />
            <path d="M7 12l3.5 3.5L17 8" stroke="#FFA40D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
            <path d="M21.44 11.05l-8.49 8.49a6 6 0 0 1-8.49-8.49l8.49-8.49a4 4 0 0 1 5.66 5.66l-8.49 8.49a2 2 0 1 1-2.83-2.83l7.78-7.78" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>

      <div className={styles.title}>
        {isLoading ? 'AI가 파일을 분석 중입니다...' :
         status === 'success' ? '자동 입력 완료!' :
         '포트폴리오 파일 첨부로 자동 입력'}
      </div>

      <p className={styles.description}>
        {isLoading ? '잠시만 기다려 주세요.' :
         status === 'success' ? '아래 항목이 자동으로 채워졌습니다. 내용을 확인해 주세요.' :
         '.PPTX, .PDF 등의 파일(이미지파일 불가)을 첨부하면 이름, 나이, 키, 몸무게 등\n프로필 항목을 파일 내용으로 자동 입력합니다.'}
      </p>

      {fileName && !isLoading && (
        <div className={styles.fileName}>{fileName}</div>
      )}

      {status === 'error' && (
        <div style={{ marginTop: 4, fontSize: 13, color: '#EF4444', fontFamily: 'Pretendard, sans-serif' }}>
          {errorMsg}
        </div>
      )}

      {status === 'idle' && (
        <p style={{ margin: 0, fontSize: 12, color: '#A2A2A2', fontFamily: 'Pretendard, sans-serif', letterSpacing: '-0.02em' }}>
          ※ AI 분석 결과는 정확하지 않을 수 있으니 입력된 내용을 반드시 확인해 주세요.
        </p>
      )}
    </div>
  );
}
